import { useState } from "react";

import Toggle from "../Toggle";
import ButtonLoader from "../ButtonLoader";

import { TrashIcon } from '@heroicons/react/24/outline';

const columnWidths = {
  index: 40,
  name: 500,
  engineering: 85,
  design: 85,
  consult: 85,
  marginPercentage: 75,
  customPrice: 95,
  consultMargin: 65,
  accepted: 65,
  actions: 50
}

const defaultItem = {
  name: '',
  engineering: 0,
  design: 0,
  consult: 0,
  customPrice: 0,
  marginPercentage: 45,
  consultMargin: false,
  accepted: false
};

const DesignAndEngineeringItemsForm = ({ project, isLoading, onSubmit }) => {
  const [items, setItems] = useState(project?.items?.length > 0 ? project.items : [defaultItem]);

  const calculateMarginPercentage = (item) => {
    if (item.customPrice && item.customPrice !== 0) {
      const totalPrice = item.engineering + item.design + item.consult;

      const percentageIncrease = ((item.customPrice - totalPrice) / totalPrice) * 100;

      return percentageIncrease;
    }

    return item.marginPercentage;
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    const formattedItems = items?.map(item => {
      const formattedItem = { ...item };

      formattedItem.marginPercentage = calculateMarginPercentage(item);
      delete formattedItem.customPrice;

      return formattedItem;
    });

    onSubmit(formattedItems);
  }

  const calculatePrice = (item) => {
    if (item.customPrice && item.customPrice !== 0) {
      return item.customPrice;
    }

    const totalPrice = item.engineering + item.design + item.consult;
    const percentageIncrease = (item.marginPercentage / 100) * totalPrice;

    return Math.ceil(totalPrice + percentageIncrease);
  }

  return (
    <form className="flex flex-col pb-1 rounded-b-lg max-h-[90vh] overflow-scroll" onSubmit={handleSubmit}>
      <div className="flex items-center h-[51px] min-h-[51px] gap-3 bg-white text-[12px] font-medium text-[#000000de] border-b border-[#0000001f]">
        <span className="text-center" style={{ width: columnWidths.index + 'px' }}>#</span>
        <span style={{ width: columnWidths.name + 'px' }}>Name</span>
        <span style={{ width: columnWidths.engineering + 'px' }}>Engineering</span>
        <span style={{ width: columnWidths.design + 'px' }}>Design</span>
        <span style={{ width: columnWidths.consult + 'px' }}>Consult</span>
        <span style={{ width: columnWidths.marginPercentage + 'px' }}>Margin %</span>
        <span style={{ width: columnWidths.customPrice + 'px' }}>Custom Price</span>
        <span style={{ width: columnWidths.consultMargin + 'px' }}>Consult %</span>
        <span style={{ width: columnWidths.accepted + 'px' }}>Accepted</span>
        <span style={{ width: columnWidths.actions + 'px' }}></span>
      </div>

      {items.map((item, index) => (
        <div
          className="flex gap-3 items-start min-h-fit-content text-sm border-b border-[#0000001f] py-2"
          style={{ background: index % 2 === 0 ? '#FAFAFA' : 'FFFFFF' }}
          key={index}
        >
          <span className="text-center" style={{ width: columnWidths.index + 'px' }}>{index + 1}</span>

          <textarea
            style={{ width: columnWidths.name + 'px', fieldSizing: 'content' }}
            className="!min-h-[32px] !px-[6px] py-1 flex items-center whitespace-pre border border-gray-300 rounded-md"
            value={item.name}
            onChange={e => {
              item.name = e.target.value;
              setItems([...items]);
            }}
          />

          <input
            type="number"
            style={{ width: columnWidths.engineering + 'px' }}
            className="!h-[32px] !px-[6px] flex items-center border border-gray-300 rounded-md"
            value={item.engineering}
            onChange={e => {
              item.engineering = Number(e.target.value);
              setItems([...items]);
            }}
          />

          <input
            type="number"
            style={{ width: columnWidths.design + 'px' }}
            className="!h-[32px] !px-[6px] flex items-center border border-gray-300 rounded-md"
            value={item.design}
            onChange={e => {
              item.design = Number(e.target.value);
              setItems([...items]);
            }}
          />

          <input
            type="number"
            style={{ width: columnWidths.consult + 'px' }}
            className="!h-[32px] !px-[6px] flex items-center border border-gray-300 rounded-md"
            value={item.consult}
            onChange={e => {
              item.consult = Number(e.target.value);
              setItems([...items]);
            }}
          />

          <input
            type="number"
            style={{ width: columnWidths.marginPercentage + 'px' }}
            className="!h-[32px] !px-[6px] flex items-center border border-gray-300 rounded-md"
            value={calculateMarginPercentage(item)}
            disabled={item.customPrice && item.customPrice !== 0}
            onChange={e => {
              item.marginPercentage = Number(e.target.value);
              setItems([...items]);
            }}
          />

          <input
            type="number"
            style={{ width: columnWidths.customPrice + 'px' }}
            className="!h-[32px] !px-[6px] flex items-center border border-gray-300 rounded-md"
            disabled={item.marginPercentage && item.marginPercentage !== 0}
            value={calculatePrice(item)}
            onChange={e => {
              item.customPrice = Number(e.target.value);
              setItems([...items]);
            }}
          />

          <div
            className="flex"
            style={{ width: columnWidths.consultMargin + 'px' }}
          >
            <Toggle
              type="checkbox"
              checked={item.customPrice ? true : item.consultMargin}
              onChecked={() => {
                item.consultMargin = !item.consultMargin;
                setItems([...items]);
              }}
            />
          </div>

          <div
            className="flex"
            style={{ width: columnWidths.accepted + 'px' }}
          >
            <Toggle
              type="checkbox"
              checked={item.accepted}
              onChecked={() => {
                item.accepted = !item.accepted;
                setItems([...items]);
              }}
            />
          </div>

          <div
            className="flex items-center"
            style={{ width: columnWidths.actions + 'px' }}
          >
            <TrashIcon
              className="cursor-pointer text-red-600 h-6 w-6"
              onClick={() => {
                items.splice(index, 1);
                setItems([...items]);
              }}
            />
          </div>
        </div>
      ))}

      <div className="flex items-center gap-2 border-t border-[#0000001f] py-4 px-4">
        <button
          type="submit"
          className="ase-button-primary h-[48px] flex items-center justify-center"
        >
          {isLoading ? <ButtonLoader /> : 'Save'}
        </button>

        <button
          type="button"
          className="ase-button-primary h-[48px]"
          onClick={() => setItems([...items, defaultItem])}
        >
          Add item
        </button>
      </div>
    </form>
  )
}

export default DesignAndEngineeringItemsForm;
